// Central SEO data — all service, area and combo pages source their copy from here.
// Kept as plain JS so we can iterate + render statically without a CMS.

export const SITE_URL = "https://www.boxb.uk";
export const SITE_NAME = "BoxB Solutions";

export const services = {
  "house-removals": {
    slug: "house-removals",
    name: "House Removals",
    shortName: "House Removals",
    tag: "Residential",
    icon: "Home",
    image: "https://images.pexels.com/photos/7310147/pexels-photo-7310147.jpeg?auto=compress&cs=tinysrgb&w=1200",
    metaTitle: "House Removals Cardiff & South Wales | BoxB Solutions",
    metaDescription:
      "Trusted house removals across Cardiff, Newport, Barry, Penarth and South Wales. Fully insured movers, careful packing, transparent pricing. Free quote in minutes.",
    lead:
      "Moving house should feel like a fresh start — not a nightmare. Our uniformed, background-checked team handles everything from studios to 5-bedroom family homes, with fixed pricing and zero surprises.",
    highlights: [
      "Fully-insured 1, 2, 3, 4 and 5-bed moves",
      "Careful furniture wrapping and floor protection included",
      "Optional professional packing and unpacking",
      "Same-day and next-day availability across South Wales",
      "Dismantle and reassemble beds, wardrobes and flat-pack",
    ],
    faqs: [
      { q: "How much does a house move cost in Cardiff?", a: "A typical 2-bed Cardiff move ranges from £180–£360 depending on distance, floor level and packing needs. We give fixed, no-obligation quotes — usually within 15 minutes of your enquiry." },
      { q: "How far in advance should I book?", a: "For weekends and end-of-month dates, book 2–3 weeks ahead. For midweek moves, same-day and next-day slots are often available." },
      { q: "Do you help with packing?", a: "Yes. We offer professional packing with premium boxes, tape, wrap and specialist protection for fragile items, TVs, and artwork." },
    ],
  },

  "office-removals": {
    slug: "office-removals",
    name: "Office Removals & Relocation",
    shortName: "Office Removals",
    tag: "Commercial",
    icon: "Building2",
    image: "https://images.pexels.com/photos/7203788/pexels-photo-7203788.jpeg?auto=compress&cs=tinysrgb&w=1200",
    metaTitle: "Office Removals & Business Relocation Cardiff | BoxB Solutions",
    metaDescription:
      "Office relocation specialists in Cardiff & South Wales. Minimum downtime, secure equipment transport, out-of-hours moves and IT-safe handling. Free site survey.",
    lead:
      "Every hour your team is offline costs money. We plan office moves down to the minute — evening and weekend slots available — so your business is up and running the next morning.",
    highlights: [
      "Out-of-hours and weekend office moves",
      "Secure handling of servers, monitors and IT kit",
      "Desk, chair and workstation dismantle / reassemble",
      "Confidential document and archive relocation",
      "Free on-site survey for offices over 500 sqft",
    ],
    faqs: [
      { q: "Can you move our office at the weekend?", a: "Yes — most Cardiff office relocations happen Friday evening through Sunday so staff walk into a working office on Monday morning." },
      { q: "Do you handle IT equipment?", a: "We do. Monitors, PCs, servers and cables are wrapped, labelled and transported in dedicated crates. We do not connect hardware — we recommend your IT provider handles reconnection." },
      { q: "Do you provide certificates for insurance?", a: "Yes, proof of Goods in Transit and Public Liability insurance is available on request before the move." },
    ],
  },

  "packing": {
    slug: "packing",
    name: "Professional Packing Services",
    shortName: "Packing",
    tag: "Add-on",
    icon: "PackageCheck",
    image: "https://images.pexels.com/photos/7464468/pexels-photo-7464468.jpeg?auto=compress&cs=tinysrgb&w=1200",
    metaTitle: "Professional Packing Services Cardiff | BoxB Solutions",
    metaDescription:
      "Professional packing, fragile-item wrap and unpacking in Cardiff, Newport & South Wales. Premium materials, damage-free guarantee. Book with your move or standalone.",
    lead:
      "Packing is where most moves go wrong. Our team packs a full 3-bed house in a single day using premium double-wall boxes, foam wrap and specialist protection for fragile items — so nothing arrives broken.",
    highlights: [
      "Premium double-wall boxes, tape, wrap and paper",
      "Specialist packing for TVs, artwork, glass and antiques",
      "Room-by-room labelling for effortless unpacking",
      "Full unpacking service on request",
      "Available as a standalone service (no move required)",
    ],
    faqs: [
      { q: "How long does packing take?", a: "As a rough guide: 1-bed = 2–4 hours, 2-bed = 4–6 hours, 3-bed = 6–8 hours, 4-bed = a full day. Two packers usually attend so it fits into a single morning or afternoon." },
      { q: "Do you supply the boxes?", a: "Yes — all materials are included in the packing quote. If you'd rather pack yourself, we can also deliver materials the day before at cost." },
      { q: "Can you pack just the fragile items?", a: "Absolutely. Half-pack (fragiles only) is a popular option that keeps costs down while removing the biggest risk." },
    ],
  },

  "end-of-tenancy-cleaning": {
    slug: "end-of-tenancy-cleaning",
    name: "End of Tenancy Cleaning",
    shortName: "End of Tenancy Cleaning",
    tag: "Cleaning",
    icon: "Sparkles",
    image: "https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg?auto=compress&cs=tinysrgb&w=1200",
    metaTitle: "End of Tenancy Cleaning Cardiff | Deposit-Back Guarantee",
    metaDescription:
      "End of tenancy cleaning in Cardiff, Newport, Barry & across South Wales. Landlord-approved checklist, oven & carpet deep clean, deposit-back friendly. Free quote.",
    lead:
      "Get your full deposit back. Our end of tenancy clean follows the standard landlord and letting-agent checklist — oven, extractor, inside cupboards, limescale, skirting, windows and carpets — so the property passes inspection first time.",
    highlights: [
      "Deep clean of oven, hob, extractor and fridge",
      "Inside all cupboards, drawers and wardrobes",
      "Limescale, skirting boards, doors and light switches",
      "Windows inside + frames + sills",
      "Optional carpet steam clean and mattress clean",
    ],
    faqs: [
      { q: "Will I get my deposit back?", a: "Our clean is built around the standard inventory checklist used by Cardiff letting agents. In the rare event your landlord flags a cleaning issue, we come back and fix it free of charge within 48 hours." },
      { q: "Do you clean carpets?", a: "Yes — professional hot-water extraction carpet cleaning is available as an add-on, and is often required by letting agents at the end of tenancy." },
      { q: "How long does it take?", a: "A 1-bed flat takes 3–4 hours; a 3-bed house takes 6–8 hours. We can usually get you a same-day or next-day slot." },
    ],
  },

  "man-and-van": {
    slug: "man-and-van",
    name: "Man and Van Cardiff",
    shortName: "Man & Van",
    tag: "Flexible",
    icon: "Truck",
    image: null,
    metaTitle: "Man and Van Cardiff | Same-Day Collection & Delivery",
    metaDescription:
      "Fast, friendly man and van service in Cardiff & South Wales. Single-item collections, small flat moves, IKEA runs, student moves. Same-day availability from £45/hr.",
    lead:
      "Just need one strong pair of hands and a van? Our man and van service is perfect for single-item collections, small flat moves, student moves and last-minute jobs — from £45 an hour.",
    highlights: [
      "Modern Luton and long-wheelbase Transit vans",
      "One or two friendly, uniformed movers",
      "Same-day availability across Cardiff & South Wales",
      "IKEA and Facebook Marketplace collections welcome",
      "Hourly or fixed-price options — you choose",
    ],
    faqs: [
      { q: "How much is a man and van in Cardiff?", a: "Rates start at £45/hour for one mover and van. For heavy or large items we recommend two movers at £65/hour. Minimum booking is 1 hour." },
      { q: "Can you collect from IKEA or a shop?", a: "Yes — we do IKEA Cardiff runs, Facebook Marketplace collections, warehouse pickups and eBay deliveries every week." },
      { q: "Is same-day available?", a: "Yes, subject to availability. Message us on WhatsApp for the fastest reply — same-day slots are often confirmed within minutes." },
    ],
  },
};

export const areas = {
  "cardiff": {
    slug: "cardiff",
    name: "Cardiff",
    metaTitle: "Movers in Cardiff | House & Office Removals — BoxB Solutions",
    metaDescription:
      "Trusted removals, packing and cleaning across Cardiff — Cathays, Roath, Cardiff Bay, Llanishen, Whitchurch, Splott and more. Same-day quotes. Fully insured.",
    lead:
      "As Cardiff locals, we know every street, every one-way, every tricky terrace load-in. From Cardiff Bay flats to family homes in Whitchurch, we move Cardiff every single day.",
    landmarks: ["Cardiff Bay", "Cathays", "Roath", "Splott", "Grangetown", "Canton", "Whitchurch", "Llanishen", "Rhiwbina", "Pontcanna"],
  },
  "newport": {
    slug: "newport",
    name: "Newport",
    metaTitle: "Removals Newport | House & Office Movers — BoxB Solutions",
    metaDescription:
      "Reliable house and office removals in Newport and Gwent. Fully insured, transparent pricing, same-day availability. Serving Malpas, Ringland, Bettws & city centre.",
    lead:
      "We run daily routes into Newport for house moves, office relocations and end of tenancy cleans — no minimum job size and no hidden mileage charges.",
    landmarks: ["Malpas", "Ringland", "Bettws", "Rogerstone", "Caerleon", "Newport City Centre"],
  },
  "swansea": {
    slug: "swansea",
    name: "Swansea",
    metaTitle: "Removals Swansea | Cardiff Movers Serving South Wales",
    metaDescription:
      "House removals, office moves and cleaning across Swansea, Mumbles, Uplands and Sketty. Fixed pricing from Cardiff-based BoxB Solutions.",
    lead:
      "Moving to or from Swansea? Our Cardiff and Swansea routes run weekly — book a two-way slot and save on long-distance mileage.",
    landmarks: ["Mumbles", "Uplands", "Sketty", "Killay", "Morriston", "Swansea Marina"],
  },
  "barry": {
    slug: "barry",
    name: "Barry",
    metaTitle: "Removals Barry & Vale of Glamorgan | BoxB Solutions",
    metaDescription:
      "House, flat and student removals in Barry, Cadoxton, Dinas Powys and Sully. Fast, insured, friendly service — free quote from BoxB Solutions.",
    lead:
      "We know the Barry lanes, the sea-front flats and the tight terraces of Cadoxton. Our team handles Barry moves with the same care we give Cardiff jobs.",
    landmarks: ["Barry Island", "Cadoxton", "Dinas Powys", "Sully", "Barry Town Centre"],
  },
  "penarth": {
    slug: "penarth",
    name: "Penarth",
    metaTitle: "Removals Penarth | Trusted Movers — BoxB Solutions",
    metaDescription:
      "Careful, insured removals throughout Penarth — including seafront apartments, town centre flats and family homes in Cosmeston.",
    lead:
      "Penarth's beautiful older buildings can be tricky — narrow stairs, tight parking, listed features. We've moved every kind of Penarth home and know how to protect them.",
    landmarks: ["Penarth Marina", "Cosmeston", "Penarth Town Centre", "Lavernock"],
  },
  "caerphilly": {
    slug: "caerphilly",
    name: "Caerphilly",
    metaTitle: "Removals Caerphilly | House & Office Movers",
    metaDescription:
      "Removals across Caerphilly, Ystrad Mynach, Bargoed and Nelson. Local team, transparent fixed prices, same-day availability. Free quote.",
    lead:
      "From Caerphilly town to Ystrad Mynach and beyond, we handle valleys moves with the same efficiency as Cardiff jobs — and often for less.",
    landmarks: ["Caerphilly Town", "Ystrad Mynach", "Bargoed", "Nelson", "Senghenydd"],
  },
  "pontypridd": {
    slug: "pontypridd",
    name: "Pontypridd",
    metaTitle: "Removals Pontypridd | Movers & Packers — BoxB Solutions",
    metaDescription:
      "House and office removals in Pontypridd, Treforest, Rhydyfelin and Cilfynydd. Insured, friendly and affordable — book online or on WhatsApp.",
    lead:
      "Whether it's a student move in Treforest or a family relocation in Rhydyfelin, Pontypridd is on our regular routes and we book last-minute slots most weeks.",
    landmarks: ["Pontypridd Town", "Treforest", "Rhydyfelin", "Cilfynydd", "Graigwen"],
  },
  "bridgend": {
    slug: "bridgend",
    name: "Bridgend",
    metaTitle: "Removals Bridgend | House Movers — BoxB Solutions",
    metaDescription:
      "Bridgend and Porthcawl removals with fixed pricing and fully insured cover. Serving Brackla, Wildmill, Sarn and Pencoed.",
    lead:
      "We run into Bridgend and Porthcawl every week. Family homes in Brackla, new-builds in Sarn, coastal flats in Porthcawl — same careful service, same fair price.",
    landmarks: ["Brackla", "Wildmill", "Sarn", "Pencoed", "Porthcawl"],
  },
};

// service × area combo pages generated from the two maps above
export const serviceAreaCombos = Object.values(services).flatMap((s) =>
  Object.values(areas).map((a) => ({
    serviceSlug: s.slug,
    areaSlug: a.slug,
    slug: `${s.slug}/${a.slug}`,
    serviceName: s.name,
    serviceShort: s.shortName,
    areaName: a.name,
  }))
);

// ----------- Blog -----------
export const blogPosts = [
  {
    slug: "how-much-does-a-house-move-cost-cardiff-2026",
    title: "How Much Does a House Move Cost in Cardiff? (2026 Prices)",
    date: "2026-02-14",
    excerpt:
      "Real 2026 Cardiff removal prices for 1-bed flats, 3-bed semis and 5-bed houses — including packing, cleaning and hidden costs to watch for.",
    tags: ["House Removals", "Pricing", "Cardiff"],
    body: [
      { type: "p", text: "One of the first questions we get asked on WhatsApp is: 'how much will my move actually cost?' Prices vary widely — some Cardiff movers quote £120 for a small flat, others quote £900 for the same job. Here's what to expect in 2026, based on the hundreds of moves we quote every month." },
      { type: "h2", text: "Typical Cardiff removal prices (2026)" },
      { type: "ul", items: [
        "Studio / 1-bed flat, local Cardiff move: £180 – £280",
        "2-bed flat or house, local move: £280 – £420",
        "3-bed house, local Cardiff: £420 – £680",
        "4-bed house, local Cardiff: £680 – £950",
        "5-bed house, local Cardiff: £950 – £1,400",
        "Cardiff → Swansea (medium distance): add £120 – £200",
        "Cardiff → London (long distance): from £1,100 upwards",
      ]},
      { type: "h2", text: "What actually drives the price?" },
      { type: "p", text: "Distance is only part of it. The three biggest cost drivers are (1) how much stuff you have, (2) how easy the property is to load — parking, stairs, lifts — and (3) whether you need packing done for you. A 2-bed flat above a shop with no parking and a lift out of service can take longer than a 3-bed detached with driveway access." },
      { type: "h2", text: "Hidden costs to watch out for" },
      { type: "ul", items: [
        "Congestion charges (Cardiff has none, but London and Bristol do)",
        "Mileage add-ons beyond a certain radius",
        "Waiting time if the new property isn't ready by the agreed slot",
        "Materials — some companies charge £3–£5 per box on top",
        "Weekend / bank holiday surcharges",
      ]},
      { type: "h2", text: "How to get an accurate quote" },
      { type: "p", text: "Send us a WhatsApp with your postcode, floor number, and a short video walk-through of the rooms. We reply with a fixed, all-in price — usually within 15 minutes. No surveyor visits, no phone tag." },
    ],
  },
  {
    slug: "end-of-tenancy-cleaning-cardiff-checklist",
    title: "The Cardiff End of Tenancy Cleaning Checklist (Landlord-Approved)",
    date: "2026-02-10",
    excerpt:
      "The exact 47-point checklist Cardiff letting agents use to release your deposit. Print it, tick it off, get your money back.",
    tags: ["Cleaning", "End of Tenancy", "Cardiff"],
    body: [
      { type: "p", text: "The single biggest reason Cardiff renters lose part of their deposit isn't damage — it's cleaning. Most letting agents follow a near-identical checklist. Here's exactly what they look at, room by room." },
      { type: "h2", text: "Kitchen (the deposit-killer)" },
      { type: "ul", items: [
        "Oven interior — including racks, trays and door glass",
        "Extractor fan filter and grease trap",
        "Hob, splashback and under the rings",
        "Fridge / freezer — defrosted, inside and behind",
        "Inside every cupboard and drawer",
        "Sink limescale, taps polished, plughole clear",
        "Skirting boards and doors wiped",
      ]},
      { type: "h2", text: "Bathroom" },
      { type: "ul", items: [
        "Limescale on taps, shower head and screen",
        "Toilet — inside, outside, behind and hinges",
        "Extractor fan cover removed and cleaned",
        "Mirror streak-free, sealant mould-free",
      ]},
      { type: "h2", text: "Living areas & bedrooms" },
      { type: "ul", items: [
        "Carpets vacuumed — steam-cleaned if agreed",
        "Windows inside, frames and sills",
        "Light switches, sockets, radiators wiped",
        "Behind and inside wardrobes",
        "Cobwebs removed from all corners",
      ]},
      { type: "h2", text: "Get it done for you" },
      { type: "p", text: "If DIY isn't your thing, book our Cardiff end of tenancy clean — it's built around this exact checklist and comes with a 48-hour re-clean guarantee if your landlord flags anything." },
    ],
  },
  {
    slug: "moving-house-checklist-uk-2026",
    title: "The UK Moving House Checklist (2026 Edition)",
    date: "2026-02-06",
    excerpt:
      "The 8-week countdown that keeps your move calm — from broadband switch to Royal Mail redirect to council tax. Print it and tick as you go.",
    tags: ["House Removals", "Checklist", "Planning"],
    body: [
      { type: "p", text: "The moves that go wrong are the ones planned in the last week. Follow this UK-specific 8-week countdown and you'll walk into your new home stress-free." },
      { type: "h2", text: "8 weeks before" },
      { type: "ul", items: [
        "Book your remover (get 3 fixed quotes)",
        "Start decluttering — bin, donate, sell",
        "Take photos of anything valuable for insurance",
      ]},
      { type: "h2", text: "4 weeks before" },
      { type: "ul", items: [
        "Order boxes and packing materials",
        "Set up Royal Mail Redirection (£36+)",
        "Tell your energy, broadband and water providers",
        "Book time off work for moving day + 1",
      ]},
      { type: "h2", text: "2 weeks before" },
      { type: "ul", items: [
        "Update your address on driving licence and V5C",
        "Notify HMRC, GP, dentist, bank",
        "Book end of tenancy cleaning (if renting)",
        "Start packing non-essentials",
      ]},
      { type: "h2", text: "Moving week" },
      { type: "ul", items: [
        "Confirm arrival time with the movers",
        "Pack an 'essentials box' — kettle, mugs, chargers, toilet roll, meds",
        "Photograph meter readings at both properties",
        "Update council tax at both councils",
      ]},
      { type: "p", text: "Save this page, share it with anyone moving soon — and message us on WhatsApp if you'd like a fixed quote for the removals side." },
    ],
  },
];

export const getService = (slug) => services[slug];
export const getArea = (slug) => areas[slug];
export const getBlogPost = (slug) => blogPosts.find((p) => p.slug === slug);
